import { jsonToHTML } from '@contentstack/utils';
import { ContentstackGatsby } from 'gatsby-source-contentstack/live-preview';
import { cloneDeep, isEmpty } from 'lodash';

type CSDataManagerConfig = {
  region: string;
  api_key: string;
  environment: string;
  branch: string;
  delivery_token: string;
  live_preview: {
    preview_token: string;
    enable: boolean;
    preview_host: string;
  };
  jsonRteToHtml: boolean;
};

/**
 *
 * An extended class of gatsby-source-contentstack's ContentstackGatsby with modified and added methods to allow live preview on locales
 * and pages added after the app has been built.
 *
 */
class CSDataManager extends ContentstackGatsby {
  static pageContentTypeUids: string[] = [
    'template_a',
    'template_b',
    'template_d',
    'template_e',
    'template_f',
    'template_h',
    'template_i',
    'template_j',
  ];

  // Using these uids to extract reference field uids for what's inside the accordion
  // or anything else that doesn't have a data_type or reference_to field and uses references inside
  static exceptionalSchemaUids = [
    'accordion',
    'accordion_entries',
    'accordion_entry',
    'nested_accordion',
    'nested_accordion_entries',
    'nested_accordion_entry',
  ];

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(config: CSDataManagerConfig) {
    super(config);
  }

  private resolveReferencePaths(
    schema: { [key: string]: unknown }[],
    path: string = '',
    refForReferencePaths: string[] = []
  ): string[] {
    const referencePaths: string[] = refForReferencePaths;
    schema.forEach((field) => {
      let refPath = path;
      if (
        field.data_type === 'blocks' ||
        !!field.reference_to ||
        field.data_type === 'reference' ||
        field.data_type === 'group' ||
        CSDataManager.exceptionalSchemaUids.includes(field.uid as string)
      ) {
        refPath += `${path === '' ? '' : '.'}${field.uid}`;
        if (field.data_type === 'reference') {
          refForReferencePaths.push(refPath);
        }
        if (!!field.blocks || !!field.schema) {
          this.resolveReferencePaths(
            field.data_type === 'blocks'
              ? (field.blocks as { [key: string]: unknown }[])
              : (field.schema as { [key: string]: unknown }[]),
            refPath,
            referencePaths
          );
        }
      }
    });
    return referencePaths;
  }

  // Checks for each page content type if the page with url exists.
  // If it does, it fetches the rest of the data, else returns undefined
  async fetchEntryByURL(url: string): Promise<{ [key: string]: unknown } | undefined> {
    const entries = Promise.all(
      CSDataManager.pageContentTypeUids.map(async (contentTypeUid) => {
        const result = await this.stackSdk
          .ContentType(contentTypeUid)
          .Query()
          .where('url', url)
          .only(['uid', 'schema', 'locale'])
          .limit(1)
          .toJSON()
          .includeContentType()
          .find();
        return result;
      })
    );
    const entryTemplateData = (await entries).find(
      (entry) => !isEmpty(entry) && !isEmpty(entry[0])
    );
    if (!entryTemplateData) {
      return;
    }

    const entryUid = entryTemplateData[0][0].uid;
    const entryLocale = entryTemplateData[0][0].locale;
    const contentTypeUid = entryTemplateData[1].uid;
    const schema = entryTemplateData.schema;

    if (!isEmpty(schema)) {
      const referencePaths: string[] = this.resolveReferencePaths(schema.schema);
      const entryWithReferences = await this.stackSdk
        .ContentType(contentTypeUid)
        .Entry(entryUid)
        .language(entryLocale)
        .toJSON()
        .includeReference(referencePaths)
        .fetch();

      // Returning the response with the same cslp__meta structure used by gatsby-source-contentstack
      return {
        ...entryWithReferences,
        cslp__meta: {
          contentTypeUid,
          entryUid,
          referencePaths,
          jsonRtePaths: [],
        },
      };
    }
  }

  private async fetchEntry(
    entryUid: string,
    contentTypeUid: string,
    referencePaths = [],
    jsonRtePaths = [],
    lang?: string
  ): Promise<unknown> {
    const fetchQuery = this.stackSdk
      .ContentType(contentTypeUid)
      .Entry(entryUid)
      .includeReference(referencePaths)
      .toJSON();
    const entry = !lang
      ? await fetchQuery.fetch()
      : await fetchQuery.language(lang).includeFallback().fetch();

    if (!isEmpty(entry)) {
      if (this.config.jsonRteToHtml) {
        jsonToHTML({
          entry: entry,
          paths: jsonRtePaths,
        });
      }
      return entry;
    }
  }

  async get<TData>(data: TData): Promise<TData> {
    if (this.stackSdk.live_preview && !this.stackSdk.live_preview?.enable) {
      console.warn(
        'Contentstack Gatsby (Live Preview): live preview is disabled in config'
      );
      return data;
    }
    if (data === null) {
      console.warn('Contentstack Gatsby (Live Preview): null was passed to get()');
      return data;
    }
    if (!this.isNested(data)) {
      console.warn('Contentstack Gatsby (Live Preview): data passed to get() is invalid');
      return data;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataCloned = cloneDeep(data) as any;
    delete dataCloned.$;

    // old method metadata
    const hasTypeNameAndUid = dataCloned.uid && dataCloned?.__typename;
    // new method metadata
    const hasCslpMetaAtRoot = dataCloned.cslp__meta;
    const multipleEntriesKey = Object.keys(dataCloned);
    const hasSingleEntry = !hasCslpMetaAtRoot && multipleEntriesKey.length === 1;
    const hasMultipleEntries = !hasCslpMetaAtRoot && multipleEntriesKey.length > 1;

    const receivedData =
      hasSingleEntry || hasMultipleEntries
        ? this.unwrapEntryData(dataCloned)
        : dataCloned;
    const { live_preview } = this.stackSdk;

    if (live_preview?.hash && live_preview.hash !== 'init') {
      this.livePreviewConfig = live_preview;

      const hasCslpMeta = receivedData.cslp__meta;

      // item can be null, since gatsby can return null
      const hasMultipleCslpMeta =
        Array.isArray(receivedData) &&
        receivedData.length > 1 &&
        receivedData.every((item) => item === null || item.cslp__meta);

      if (!hasCslpMeta && !hasMultipleCslpMeta && !hasTypeNameAndUid) {
        throw new Error(
          'Contentstack Gatsby (Live Preview): Entry data must contain cslp__meta for live preview'
        );
      }

      if (hasMultipleCslpMeta) {
        try {
          const multipleLPEntries = await Promise.all(
            receivedData.map((item) => {
              if (item === null) {
                return Promise.resolve(null);
              }
              return this.fetchEntry(
                item.cslp__meta.entryUid,
                item.cslp__meta.contentTypeUid,
                item.cslp__meta.refPaths,
                item.cslp__meta.rtePaths,
                item.locale
              );
            })
          );
          const result: { [key: string]: unknown } = {};
          multipleEntriesKey.forEach((key: string, index) => {
            result[key] = multipleLPEntries[index];
          });
          return result as TData;
        } catch (error) {
          console.error('Contentstack Gatsby (Live Preview):', error);
          return dataCloned;
        }
      } else if (hasCslpMeta) {
        const entryCslpMeta = receivedData.cslp__meta;
        const contentTypeUid = entryCslpMeta.contentTypeUid;
        const entryUid = entryCslpMeta.entryUid;

        if (!entryUid || !contentTypeUid) {
          console.warn(
            'Contentstack Gatsby (Live Preview): no entry uid or content type uid was found inside cslp__meta'
          );
          return dataCloned;
        }

        const refPaths = entryCslpMeta.referencePaths ?? [];
        const rtePaths = entryCslpMeta.jsonRtePaths ?? [];
        const locale = receivedData.locale;
        const entry = await this.fetchEntry(
          entryUid,
          contentTypeUid,
          refPaths,
          rtePaths,
          locale
        );
        return entry as TData;
      } else if (hasTypeNameAndUid) {
        const clonedData = await this.getUsingTypeName(dataCloned);
        return clonedData;
      }
    }
    return dataCloned;
  }
}

export default CSDataManager;
